import React from "react";
import { Button, LoadingIndicator } from "@lululemon/ui-library";
import PropTypes from "prop-types";
import cs from "classnames";
import { useDeviceType } from "@lululemon/ecom-site/src/util/with-device-characteristics";

import ReelVideoControls from "../reel-video-controls/reel-video-controls";
import { GesturesNavigationControls } from "../reel-navigation-controls/reel-navigation-controls";
import { useStoryNavigation } from "../utils/reel-navigation-context";

import styles from "./reel-overlay.module.scss";

const ReelOverlay = ({
  ctaData,
  dispatchInstaStoryAction,
  isMediaLoading,
  storyType,
  videoRef,
  watchAgainText,
}) => {
  const { deviceType } = useDeviceType();
  const { hasPresentedAllStories, setHasPresentedAllStories, resetStoryIndex } =
    useStoryNavigation();

  const isVideoStory = storyType === "videoReel";
  const showWatchAgainCTA = hasPresentedAllStories && !!watchAgainText;
  const showStoryControls = !isMediaLoading && !showWatchAgainCTA;

  const resetReel = () => {
    setHasPresentedAllStories(false);
    resetStoryIndex();
    setTimeout(async () => {
      await videoRef.current?.play();
      dispatchInstaStoryAction("play");
    }, 200);
  };

  React.useEffect(() => {
    if (showWatchAgainCTA && videoRef.current) {
      videoRef.current?.pause();
    }
  }, [showWatchAgainCTA, videoRef]);

  return (
    <div
      className={cs(styles.reelOverlay, {
        [styles.reelOverlayDimmed]: showWatchAgainCTA,
        [styles.reelOverlayGradientBottomNoCTA]:
          !ctaData?.url && !ctaData?.text && isVideoStory,
        [styles.reelOverlayGradientBottomWithCTA]:
          ctaData?.url && ctaData?.text && isVideoStory,
      })}
      data-testid="overlay-container"
    >
      {showStoryControls && (
        <>
          {isVideoStory && (
            <ReelVideoControls
              dispatchInstaStoryAction={dispatchInstaStoryAction}
              videoRef={videoRef}
            />
          )}
          {ctaData?.url && ctaData?.text && (
            <Button
              className={styles.reelCTA}
              block
              href={ctaData?.url}
              variant="outline"
            >
              {ctaData.text}
            </Button>
          )}
        </>
      )}
      {(isMediaLoading || showWatchAgainCTA) && (
        <div className={styles.reelCenteredContainer}>
          {isMediaLoading && <LoadingIndicator />}
          {showWatchAgainCTA && (
            <button
              aria-label="All slides finished. Replay all?"
              className={styles.reelWatchAgainCTA}
              onClick={resetReel}
            >
              {watchAgainText}
            </button>
          )}
        </div>
      )}
      {deviceType.isMobile && !hasPresentedAllStories && (
        <GesturesNavigationControls
          dispatchInstaStoryAction={dispatchInstaStoryAction}
          videoRef={videoRef}
        />
      )}
    </div>
  );
};

ReelOverlay.defaultProps = {
  ctaData: null,
  header: null,
  videoRef: null,
  watchAgainText: null,
};

ReelOverlay.propTypes = {
  ctaData: PropTypes.shape({
    text: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  dispatchInstaStoryAction: PropTypes.func.isRequired,
  isMediaLoading: PropTypes.bool.isRequired,
  storyType: PropTypes.string.isRequired,
  videoRef: PropTypes.object,
  watchAgainText: PropTypes.string,
};

export default ReelOverlay;
